import {Card, DataStatus} from 'platform/components';
import {Grid, Hide, Show, Space} from 'platform/foundation';

import {isNil} from 'ramda';

import {NewOfferPurchaseBrokerageVehicleResponseBody} from '@dms/api/finance';
import {
  useGetPurchaseBusinessCasePricesOverviewQuery,
  useGetSaleBusinessCasePricesOverviewQuery,
} from '@dms/api/sales';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {PriceIndicator, usePricePermissions} from '@dms/shared';

import {PriceNotAvailable} from '../../../components/PriceNotAvailable/PriceNotAvailable';
import {useBusinessCase} from '../../../hooks/useBusinessCase';
import {getSalePriceVisibility} from '../../../utils/getSalePriceVisibility';

type FinancialOverviewProps = {
  businessCaseId: string;
};

// eslint-disable-next-line complexity, max-lines-per-function -- Pre-existing violation
export const FinancialOverview = ({businessCaseId}: FinancialOverviewProps) => {
  const {
    sellingVehicleId,
    state,
    offer: _offer,
    isSelling,
    isBrokerage,
    isBuying,
    purchaseVehicles,
  } = useBusinessCase(businessCaseId);

  const {data: saleVehicleData} = useGetSaleVehicleQuery(
    {vehicleId: sellingVehicleId ?? ''},
    {skip: isNil(sellingVehicleId)}
  );

  const purchaseVehicle = isBrokerage
    ? (purchaseVehicles[0] as NewOfferPurchaseBrokerageVehicleResponseBody)
    : undefined;
  const {
    data: purchaseVehiclePrices,
    isLoading: isLoadingPurchaseVehiclePrices,
    isError: isPurchaseVehiclePricesError,
  } = useGetPurchaseBusinessCasePricesOverviewQuery(
    {businessCaseId},
    {skip: state === 'CONCEPT' || isSelling}
  );
  const {
    data: saleVehiclePrices,
    isLoading: isLoadingSaleVehiclePrices,
    isError: isSaleVehiclePricesError,
  } = useGetSaleBusinessCasePricesOverviewQuery(
    {businessCaseId},
    {skip: state === 'CONCEPT' || isBuying}
  );

  const pricePermissions = usePricePermissions({
    vehicleRecordId: sellingVehicleId || purchaseVehicle?.vehicleId,
    businessCaseRecordId: businessCaseId,
  });
  const {
    canReadVehicleProfit,
    canReadVehicleSellingPrice,
    canReadBusinessCaseSaleVehiclePurchasePrice,
    canReadBusinessCaseSaleTotalPrice,
    canReadVehiclePurchasePrice,
    canReadBusinessCasePurchaseVehiclePurchasePrice,
    canReadBusinessCasePurchaseExpectedProfit,
    canReadVehicleCosts,
    canReadVehicleEarnings,
    canReadBusinessCaseBrokerageFees,
  } = pricePermissions;

  const canReadPurchasePrice =
    canReadVehiclePurchasePrice && canReadBusinessCasePurchaseVehiclePurchasePrice;
  const {
    canReadSellingPrice,
    canReadSaleMargins,
    canReadExpectedEarnings,
    isUpsellNotAvailable,
    isDiscountNotAvailable,
    isRealCostsNotAvailable,
  } = getSalePriceVisibility(pricePermissions, saleVehiclePrices);

  const isVatDeductible = Boolean(
    saleVehicleData?.isVatDeductible ?? purchaseVehicle?.pricing?.vatDeductible
  );

  return (
    <Card title={i18n.t('entity.order.labels.finances')}>
      <Grid columns={[1, 2, 4]}>
        {/* case for selling + selling brokerage BC*/}
        <Show when={isSelling}>
          <DataStatus
            isEmpty={isNil(saleVehiclePrices)}
            isLoading={isLoadingSaleVehiclePrices}
            isError={isSaleVehiclePricesError}
            minHeight={10}
          >
            <Show when={canReadSellingPrice}>
              <PriceIndicator
                size="large"
                label={i18n.t`entity.businessCase.labels.vehicleSellingPrice`}
                data-testid={testIds.businessCase.overview('sellingPrice')}
                value={saleVehiclePrices?.vehicleSellingPrice?.withVat}
                valueWithoutVat={saleVehiclePrices?.vehicleSellingPrice?.withoutVat}
                alwaysShowWithoutVatValue={isVatDeductible}
              />
            </Show>
            <Hide when={canReadSellingPrice}>
              <Space fillAvailable />
            </Hide>
            <Show when={isUpsellNotAvailable}>
              <PriceNotAvailable
                data-testid={testIds.businessCase.overview('summary-upsell')}
                title={i18n.t('entity.businessCase.labels.upsell')}
              />
            </Show>
            <Hide when={isUpsellNotAvailable}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.overview('summary-upsell')}
                value={saleVehiclePrices?.upsell?.withVat}
                valueWithoutVat={saleVehiclePrices?.upsell?.withoutVat}
                label={i18n.t('entity.businessCase.labels.upsell')}
              />
            </Hide>
            <Hide when={isBrokerage || saleVehicleData?.type === 'BROKERAGE'}>
              <Show when={isDiscountNotAvailable}>
                <PriceNotAvailable
                  data-testid={testIds.businessCase.overview('summary-discount')}
                  title={i18n.t('general.labels.discount')}
                />
              </Show>
              <Hide when={isDiscountNotAvailable}>
                <PriceIndicator
                  size="large"
                  data-testid={testIds.businessCase.overview('summary-discount')}
                  value={saleVehiclePrices?.discount?.withVat}
                  valueWithoutVat={saleVehiclePrices?.discount?.withoutVat}
                  areInvertedNumbers
                  label={i18n.t('general.labels.discount')}
                  alwaysShowWithoutVatValue={isVatDeductible}
                />
              </Hide>
            </Hide>
            <Show when={isBrokerage || saleVehicleData?.type === 'BROKERAGE'}>
              <Space fillAvailable />
            </Show>
            <Show when={canReadSellingPrice && canReadBusinessCaseSaleTotalPrice}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.overview('totalPrice')}
                label={i18n.t`entity.businessCase.labels.totalPrice`}
                value={saleVehiclePrices?.totalPrice?.withVat}
                valueWithoutVat={saleVehiclePrices?.totalPrice?.withoutVat}
              />
            </Show>
            <Hide when={canReadSellingPrice && canReadBusinessCaseSaleTotalPrice}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadPurchasePrice && canReadVehicleCosts}>
              <Show when={isRealCostsNotAvailable}>
                <PriceNotAvailable
                  data-testid={testIds.businessCase.overview('summary-realCosts')}
                  title={i18n.t('entity.businessCase.labels.realCosts')}
                />
              </Show>
              <Hide when={isRealCostsNotAvailable}>
                <PriceIndicator
                  size="large"
                  data-testid={testIds.businessCase.overview('summary-realCosts')}
                  label={i18n.t('entity.businessCase.labels.realCosts')}
                  labelTooltip={i18n.t('entity.businessCase.labels.realCostsTooltip')}
                  value={saleVehiclePrices?.realCosts}
                />
              </Hide>
            </Show>
            <Hide when={canReadPurchasePrice && canReadVehicleCosts}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadExpectedEarnings}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.overview('summary-totalExpectedEarnings')}
                value={saleVehiclePrices?.expectedEarnings}
                label={i18n.t('entity.businessCase.labels.totalExpectedEarnings')}
                labelTooltip={i18n.t('entity.businessCase.labels.totalExpectedEarningsTooltip')}
              />
            </Show>
            <Hide when={canReadExpectedEarnings}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadSaleMargins}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.overview('summary-totalGrossMargin')}
                label={i18n.t('entity.businessCase.labels.grossMarginOnVehicle')}
                value={saleVehiclePrices?.vehicleMargin?.amount}
                tag={Number(saleVehiclePrices?.vehicleMargin?.percentage)}
                tagType="percent"
                isHighligted
              />
            </Show>
            <Hide when={canReadSaleMargins}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadSaleMargins}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.overview('summary-grossMarginTotal')}
                label={i18n.t('entity.businessCase.labels.grossMarginTotal')}
                isHighligted
                value={saleVehiclePrices?.totalMargin?.amount}
                tag={Number(saleVehiclePrices?.totalMargin?.percentage)}
                tagType="percent"
              />
            </Show>
            <Hide when={canReadSaleMargins}>
              <Space fillAvailable />
            </Hide>
          </DataStatus>
        </Show>
        {/* case only for buying BC*/}
        <Show when={isBuying}>
          <DataStatus
            isEmpty={isNil(purchaseVehiclePrices)}
            isLoading={isLoadingPurchaseVehiclePrices}
            isError={isPurchaseVehiclePricesError}
            minHeight={10}
          >
            <Show when={canReadPurchasePrice}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-buyingPrice')}
                label={i18n.t('entity.businessCase.labels.vehicleBuyingPrice')}
                value={purchaseVehiclePrices?.vehiclePurchasePrice?.withVat}
                valueWithoutVat={purchaseVehiclePrices?.vehiclePurchasePrice?.withoutVat}
                alwaysShowWithoutVatValue={isVatDeductible}
              />
            </Show>
            <Hide when={canReadVehiclePurchasePrice}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadVehicleCosts}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-costs')}
                value={purchaseVehiclePrices?.additionalCosts}
                label={i18n.t('entity.businessCase.labels.additionalExpectedCosts')}
                labelTooltip={i18n.t('entity.businessCase.labels.additionalExpectedCostsTooltip')}
              />
            </Show>
            <Hide when={canReadVehicleCosts}>
              <Space fillAvailable />
            </Hide>
            <Show
              when={
                canReadVehicleCosts &&
                canReadBusinessCaseSaleVehiclePurchasePrice &&
                canReadVehiclePurchasePrice
              }
            >
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-totalExpectedCosts')}
                value={purchaseVehiclePrices?.totalCosts}
                label={i18n.t('entity.businessCase.labels.totalExpectedCosts')}
              />
            </Show>
            <Hide
              when={
                canReadVehicleCosts && canReadVehicleSellingPrice && canReadVehiclePurchasePrice
              }
            >
              <Space fillAvailable />
            </Hide>
            <Show when={canReadVehicleProfit}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-vehicleGrossMargin')}
                isHighligted
                tag={Number(purchaseVehiclePrices?.vehicleMargin?.percentage)}
                tagType="percent"
                value={purchaseVehiclePrices?.vehicleMargin?.amount}
                label={i18n.t('entity.businessCase.labels.vehicleGrossMargin')}
                labelTooltip={i18n.t('entity.businessCase.labels.vehicleGrossMarginTooltip')}
              />
            </Show>
            <Hide when={canReadVehicleProfit}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadVehicleSellingPrice}>
              <PriceIndicator
                size="large"
                label={i18n.t('entity.businessCase.labels.vehicleSellingPrice')}
                data-testid={testIds.businessCase.buying('summary-vehicleSellingPrice')}
                value={purchaseVehiclePrices?.vehicleSellingPrice?.withVat}
                valueWithoutVat={purchaseVehiclePrices?.vehicleSellingPrice?.withoutVat}
                alwaysShowWithoutVatValue={isVatDeductible}
              />
            </Show>
            <Hide when={canReadVehicleSellingPrice}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadVehicleEarnings && canReadBusinessCasePurchaseExpectedProfit}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-expectedEarnings')}
                label={i18n.t('entity.businessCase.labels.expectedEarnings')}
                labelTooltip={i18n.t('entity.businessCase.labels.expectedEarningsTooltip')}
                value={purchaseVehiclePrices?.additionalEarnings}
              />
            </Show>
            <Hide when={canReadVehicleEarnings && canReadBusinessCasePurchaseExpectedProfit}>
              <Space fillAvailable />
            </Hide>
            <Show
              when={
                canReadVehicleEarnings && canReadVehicleSellingPrice && canReadVehiclePurchasePrice
              }
            >
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-realEarnings')}
                label={i18n.t('entity.businessCase.labels.totalExpectedEarnings')}
                value={purchaseVehiclePrices?.totalEarnings}
              />
            </Show>
            <Hide
              when={
                canReadVehicleEarnings && canReadVehicleSellingPrice && canReadVehiclePurchasePrice
              }
            >
              <Space fillAvailable />
            </Hide>
            <Show
              when={
                canReadVehicleEarnings && canReadVehicleSellingPrice && canReadVehiclePurchasePrice
              }
            >
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-totalGrossMargin')}
                label={i18n.t('entity.businessCase.labels.totalGrossMargin')}
                labelTooltip={i18n.t('entity.businessCase.labels.totalGrossMarginTooltip')}
                value={purchaseVehiclePrices?.totalMargin?.amount}
                isHighligted
                tag={Number(purchaseVehiclePrices?.totalMargin?.percentage)}
                tagType="percent"
              />
            </Show>
            <Hide
              when={
                canReadVehicleEarnings && canReadVehicleSellingPrice && canReadVehiclePurchasePrice
              }
            >
              <Space fillAvailable />
            </Hide>
          </DataStatus>
        </Show>
        {/* case only for buying brokerage BC*/}
        <Show when={isBrokerage && !isSelling && !isBuying}>
          <DataStatus
            isEmpty={isNil(purchaseVehiclePrices)}
            isLoading={isLoadingPurchaseVehiclePrices}
            isError={isPurchaseVehiclePricesError}
            minHeight={10}
          >
            <Show when={canReadSellingPrice}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-vehicleSellingPrice')}
                value={purchaseVehiclePrices?.vehicleSellingPrice?.withVat}
                valueWithoutVat={purchaseVehiclePrices?.vehicleSellingPrice?.withoutVat}
                label={i18n.t('entity.businessCase.labels.vehicleSellingPrice')}
              />
            </Show>
            <Hide when={canReadSellingPrice}>
              <Space fillAvailable />
            </Hide>
            <Show when={canReadVehicleCosts}>
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-totalExpectedCosts')}
                value={purchaseVehiclePrices?.totalCosts}
                label={i18n.t('entity.businessCase.labels.totalExpectedCosts')}
              />
            </Show>
            <Hide when={canReadVehicleCosts}>
              <Space fillAvailable />
            </Hide>
            <Show
              when={
                canReadPurchasePrice &&
                canReadSellingPrice &&
                canReadVehicleEarnings &&
                canReadBusinessCaseBrokerageFees
              }
            >
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-vehicleGrossMargin')}
                tag={Number(purchaseVehiclePrices?.vehicleMargin?.percentage)}
                tagType="percent"
                value={purchaseVehiclePrices?.vehicleMargin?.amount}
                label={i18n.t('entity.businessCase.labels.vehicleGrossMargin')}
                labelTooltip={i18n.t('entity.businessCase.labels.vehicleGrossMarginTooltip')}
              />
              <PriceIndicator
                size="large"
                data-testid={testIds.businessCase.buying('summary-totalGrossMargin')}
                tag={Number(purchaseVehiclePrices?.totalMargin?.percentage)}
                tagType="percent"
                value={purchaseVehiclePrices?.totalMargin?.amount}
                label={i18n.t('entity.businessCase.labels.totalGrossMargin')}
                labelTooltip={i18n.t('entity.businessCase.labels.totalGrossMarginTooltip')}
              />
            </Show>
            <Hide
              when={
                canReadPurchasePrice &&
                canReadSellingPrice &&
                canReadVehicleEarnings &&
                canReadBusinessCaseBrokerageFees
              }
            >
              <Space fillAvailable />
              <Space fillAvailable />
            </Hide>
          </DataStatus>
        </Show>
      </Grid>
    </Card>
  );
};
